/**
 * Phase 3 (Feb 2026) — Campaign-Briefing client (Chat + Wizard).
 *
 * Thin persistence layer that maintains the shared briefing row that
 * both the Profi-Chat widget and the Campaign Wizard read + write.
 *
 * Backing store: /api/v1/campaign-briefings/*
 * Local cache : `sessionStorage[VIONA_BRIEFING_ID]` to survive reload
 *               inside the same tab (server row is the source of truth).
 *
 * Conflict handling: PUT uses `If-Match: <object_version>`. A 409
 * response carries the current row; callers must render a merge / reload
 * prompt (the wizard shows a "Chat hat den Brief geändert" banner and
 * offers reload).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

const SS_KEY = "viona.briefing.active_id";

export function useActiveBriefing() {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conflict, setConflict] = useState(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;                       // reset on re-mount
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/campaign-briefings/active");
      if (!mounted.current) return;
      setBriefing(r.data);
      try {
        sessionStorage.setItem(SS_KEY, r.data.id);
      } catch (e) {
        // Silent — sessionStorage may be blocked in some browsers.
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(
    async ({ steps, current_step, origin = "wizard", status = "draft" }) => {
      if (!briefing) return null;
      setConflict(null);
      try {
        const r = await api.put(
          `/campaign-briefings/${briefing.id}`,
          { steps, current_step, origin, status },
          { headers: { "If-Match": String(briefing.object_version) } },
        );
        if (mounted.current) setBriefing(r.data);
        return r.data;
      } catch (e) {
        if (e?.response?.status === 409) {
          const current = e.response.data?.detail?.current;
          if (current && mounted.current) {
            setBriefing(current);
            setConflict({
              expected: e.response.data?.detail?.expected_version,
              current_version: current.object_version,
              last_source: current.last_written_source,
            });
          }
          return null;
        }
        throw e;
      }
    },
    [briefing],
  );

  const abandon = useCallback(async () => {
    if (!briefing) return;
    await api.post(`/campaign-briefings/${briefing.id}/abandon`);
    try { sessionStorage.removeItem(SS_KEY); } catch (e) { /* silent */ }
    await load();
  }, [briefing, load]);

  const refresh = useCallback(async () => {
    if (!briefing) return;
    const r = await api.get(`/campaign-briefings/${briefing.id}`);
    if (mounted.current) {
      setBriefing(r.data);
      setConflict(null);
    }
  }, [briefing]);

  return { briefing, loading, save, abandon, refresh, conflict,
           dismissConflict: () => setConflict(null) };
}
